import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Button, Label, NumberInput, Select, Switch, TextInput, Textarea } from '../../../components/ui';
import { ChevronLeftIcon } from '../../../components/ui/Icons';
import { useMgaSheetColumn } from './useData';
import { moduleConfig, getInitialFormData, mapItemToFormData } from './config';

const COLUMN_TYPE_OPTIONS = [
  { value: 'EARNING', label: 'Earning' },
  { value: 'DEDUCTION', label: 'Deduction' },
];

const POSTING_BUCKETS = [
  { value: '', label: 'None' },
  { value: 'ALLOTMENT', label: 'Allotment' },
  { value: 'CASH_ADVANCE', label: 'Cash advance' },
  { value: 'BONDED', label: 'Bonded' },
  { value: 'DEDUCTIONS', label: 'Deductions' },
];

const Form: React.FC = () => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const editId = id ? Number(id) : NaN;
  const isEdit = Number.isFinite(editId);
  const { item, isLoading, isError } = useMgaSheetColumn(isEdit ? editId : 0);
  const [form, setForm] = useState(getInitialFormData);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (item) setForm(mapItemToFormData(item));
  }, [item]);

  const isSystem = isEdit && item && item.behavior !== 'MANUAL';
  const isInUse = isEdit && item?.inUse === true;
  const isLocked = Boolean(isSystem || isInUse);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const payload = {
        ...form,
        postingBucket: form.postingBucket || null,
        notes: form.notes?.trim() || null,
      };
      if (isEdit) {
        const { code: _c, ...rest } = payload;
        await moduleConfig.form.update(editId, rest);
      } else {
        await moduleConfig.form.create(payload);
      }
      navigate(moduleConfig.module.route.list);
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? String((err as { response?: { data?: { message?: string } } }).response?.data?.message ?? '')
          : '';
      setError(msg || moduleConfig.form.errorMessage);
    } finally {
      setBusy(false);
    }
  };

  if (isEdit && isLoading) {
    return <p className={cn('p-6 text-sm', colors.textMuted)}>{moduleConfig.form.loadingText}</p>;
  }
  if (isEdit && (isError || !item)) {
    return <p className="p-6 text-sm text-red-600">{moduleConfig.detail.notFoundMessage}</p>;
  }

  const title = isEdit ? moduleConfig.form.edit.title : moduleConfig.form.new.title;
  const subtitle = isEdit ? moduleConfig.form.edit.subtitle : moduleConfig.form.new.subtitle;

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Link
          to={moduleConfig.module.route.list}
          className={cn('inline-flex items-center gap-1 text-sm', colors.primaryFg)}
        >
          <ChevronLeftIcon className="h-4 w-4" />
          Back
        </Link>
      </div>
      <div>
        <h1 className={cn('text-xl font-semibold', colors.text)}>{title}</h1>
        <p className={cn('text-sm mt-1', colors.textMuted)}>{subtitle}</p>
        {isLocked && (
          <p className="text-sm text-amber-600 mt-2">
            {isSystem
              ? `System column (${item?.behavior}) — only label, order, and visibility can be changed.`
              : 'This column is in use on MGA periods and cannot be deleted. You can still edit label, order, and visibility.'}
          </p>
        )}
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        {!isEdit && (
          <div>
            <Label htmlFor="code">Code</Label>
            <TextInput
              id="code"
              value={form.code ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
              placeholder="e.g. travelAdvance"
              required
            />
            <p className={cn('text-xs mt-1', colors.textMuted)}>
              camelCase identifier; used in API and storage.
            </p>
          </div>
        )}
        {isEdit && (
          <div>
            <Label>Code</Label>
            <p className={cn('text-sm font-mono', colors.text)}>{item?.code}</p>
          </div>
        )}
        <div>
          <Label htmlFor="name">Name</Label>
          <TextInput
            id="name"
            value={form.name ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="columnType">Type</Label>
            <Select
              id="columnType"
              value={form.columnType ?? 'DEDUCTION'}
              onChange={(e) =>
                setForm((f) => ({ ...f, columnType: e.target.value as typeof form.columnType }))
              }
              options={COLUMN_TYPE_OPTIONS}
              disabled={Boolean(isSystem)}
              showClearButton={false}
            />
          </div>
          <div>
            <Label htmlFor="sortOrder">Sort order</Label>
            <NumberInput
              id="sortOrder"
              value={String(form.sortOrder ?? 0)}
              onChange={(e) => setForm((f) => ({ ...f, sortOrder: Number(e.target.value) || 0 }))}
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-6">
          <label className="flex items-center gap-2 text-sm">
            <Switch
              checked={Boolean(form.includeInTotal)}
              onChange={(e) => setForm((f) => ({ ...f, includeInTotal: e.target.checked }))}
              disabled={Boolean(isSystem)}
            />
            Include in total
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Switch
              checked={Boolean(form.showInGrid)}
              onChange={(e) => setForm((f) => ({ ...f, showInGrid: e.target.checked }))}
            />
            Show in grid
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Switch
              checked={Boolean(form.isActive)}
              onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
            />
            Active
          </label>
        </div>
        <div>
          <Label htmlFor="postingBucket">Posting bucket</Label>
          <Select
            id="postingBucket"
            value={form.postingBucket ?? ''}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                postingBucket: (e.target.value || null) as typeof form.postingBucket,
              }))
            }
            options={POSTING_BUCKETS}
            disabled={Boolean(isSystem)}
            showClearButton={false}
          />
        </div>
        <div>
          <Label htmlFor="notes">Notes</Label>
          <Textarea
            id="notes"
            value={form.notes ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            rows={3}
          />
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex gap-2 pt-2">
          <Button type="submit" disabled={busy}>
            {isEdit ? moduleConfig.form.edit.submitText : moduleConfig.form.new.submitText}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate(moduleConfig.module.route.list)}>
            {moduleConfig.form.cancelText}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default Form;
